import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../components/ui/command";
import { compressImage } from "../utils/imageCompression";
import { useCompressionSettings } from "../utils/CompressionContext";
import { StepFormDialog } from "../components/steps/StepFormDialog";
import { StepCard } from "../components/steps/StepCard";
import { Plus, ListChecks, Loader2, ChevronsUpDown } from "lucide-react";

export default function StepsPage() {
  const { token } = useAuth();
  const compressionSettings = useCompressionSettings();
  const fileInputRef = useRef(null);
  const [products, setProducts] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [phases, setPhases] = useState([]);
  const [cvTemplates, setCvTemplates] = useState({});
  const [steps, setSteps] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState("");
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingStep, setEditingStep] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [expandedStep, setExpandedStep] = useState(null);
  const [uploadingRef, setUploadingRef] = useState(null);
  const [productPickerOpen, setProductPickerOpen] = useState(false);

  const [formData, setFormData] = useState({
    product_id: "",
    step_number: 1,
    name: "",
    description: "",
    phase: "phase_1",
    phase_id: "",
    cv_task: "",
    reference_image_base64: null,
    expected_values: {},
    pass_criteria: "",
    mandatory: false,
    is_optional: false,

    report_mapping: null,
    qc_mappings: [],
    conditional_expected_values: [],
    step_condition: null,
    gauge_config: null,
  });

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchProducts = useCallback(async () => {
    try {
      const [prodRes, typeRes] = await Promise.all([
        axios.get(`${API}/products`, { headers }),
        axios.get(`${API}/product-types`, { headers }),
      ]);
      setProducts(prodRes.data);
      setProductTypes(typeRes.data);
      if (prodRes.data.length > 0) { setSelectedProduct(prodRes.data[0].id); }
    } catch (error) {
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  }, [headers]);

  const fetchPhases = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/phases`, { headers });
      setPhases(response.data);
    } catch (error) {
      console.error("Failed to load phases");
    }
  }, [headers]);

  const fetchCvTemplates = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/cv-templates`, { headers });
      setCvTemplates(response.data);
    } catch (error) {
      console.error("Failed to load CV templates");
    }
  }, [headers]);

  const fetchSteps = useCallback(async (productId) => {
    if (!productId) return;
    try {
      const response = await axios.get(`${API}/steps?product_id=${productId}`, { headers });
      setSteps(response.data);
    } catch (error) {
      toast.error("Failed to load steps");
    }
  }, [headers]);

  useEffect(() => {
    fetchProducts();
    fetchPhases();
    fetchCvTemplates();
  }, [fetchProducts, fetchPhases, fetchCvTemplates]);

  useEffect(() => {
    if (selectedProduct) { fetchSteps(selectedProduct); }
  }, [selectedProduct, fetchSteps]);

  const normalizeStepCondition = (stepCondition) => {
    if (!stepCondition) return null;
    if (stepCondition.conditions) return stepCondition;
    // legacy shape: a single {when_field, when_value} pair
    if (stepCondition.when_field) {
      return { conditions: [{ when_field: stepCondition.when_field, when_values: [stepCondition.when_value].filter(v => v !== undefined && v !== "") }] };
    }
    return null;
  };

  const handleOpenDialog = (step = null) => {
    if (step) {
      setEditingStep(step);
      setFormData({
        product_id: step.product_id,
        step_number: step.step_number,
        name: step.name,
        description: step.description,
        phase: step.phase,
        phase_id: step.phase_id || "",
        cv_task: step.cv_task,
        reference_image_base64: step.reference_image_base64,
        expected_values: step.expected_values || {},
        pass_criteria: step.pass_criteria || "",
        mandatory: step.mandatory || false,
        is_optional: step.is_optional || false,

        report_mapping: step.report_mapping || null,
        qc_mappings: step.qc_mappings || (step.qc_mapping ? [step.qc_mapping] : []),
        conditional_expected_values: step.conditional_expected_values || [],
        ocr_validation: step.ocr_validation || null,
        step_condition: normalizeStepCondition(step.step_condition),
        gauge_config: step.gauge_config || null,
      });
    } else {
      setEditingStep(null);
      const defaultPhase = phases.length > 0 ? phases[0] : null;
      setFormData({
        product_id: selectedProduct,
        step_number: steps.length + 1,
        name: "",
        description: "",
        phase: defaultPhase?.code || "phase_1",
        phase_id: defaultPhase?.id || "",
        cv_task: "",
        reference_image_base64: null,
        expected_values: {},
        pass_criteria: "",
        qc_mappings: [],
        conditional_expected_values: [],
        step_condition: null,
        gauge_config: null,
      });
    }
    setShowDialog(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please upload an image file"); return; }
    try {
      toast.loading("Compressing image...", { id: "compress-step" });
      const compressedBase64 = await compressImage(file, {
        maxWidth: compressionSettings.maxWidth,
        maxHeight: compressionSettings.maxHeight,
        quality: compressionSettings.quality,
        maxSizeKB: compressionSettings.maxSizeKB
      });
      setFormData({ ...formData, reference_image_base64: compressedBase64 });
      toast.success("Image compressed!", { id: "compress-step" });
    } catch (error) {
      toast.error("Failed to process image", { id: "compress-step" });
      console.error("Compression error:", error);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) { toast.error("Step name is required"); return; }
    setSubmitting(true);
    try {
      if (editingStep) {
        await axios.put(`${API}/steps/${editingStep.id}`, formData, { headers });
        toast.success("Step updated");
      } else {
        await axios.post(`${API}/steps`, formData, { headers });
        toast.success("Step created");
      }
      setShowDialog(false);
      fetchSteps(selectedProduct);
    } catch (error) {
      toast.error("Operation failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this step?")) return;
    try {
      await axios.delete(`${API}/steps/${id}`, { headers });
      toast.success("Step deleted");
      fetchSteps(selectedProduct);
    } catch (error) {
      toast.error("Failed to delete step");
    }
  };

  const handleQuickRefUpload = async (stepId, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please upload an image file"); return; }

    setUploadingRef(stepId);
    try {
      toast.loading("Compressing image...", { id: `compress-${stepId}` });
      const compressedBase64 = await compressImage(file, {
        maxWidth: compressionSettings.maxWidth,
        maxHeight: compressionSettings.maxHeight,
        quality: compressionSettings.quality,
        maxSizeKB: compressionSettings.maxSizeKB
      });

      await axios.put(`${API}/steps/${stepId}/reference-image`,
        { reference_image_base64: compressedBase64 },
        { headers }
      );
      toast.success("Reference image uploaded!", { id: `compress-${stepId}` });
      fetchSteps(selectedProduct);
    } catch (error) {
      toast.error("Failed to upload reference image", { id: `compress-${stepId}` });
    } finally {
      setUploadingRef(null);
    }
  };

const getPhaseColor = (phase, phaseId) => {
    if (phaseId) {
      const phaseObj = phases.find(p => p.id === phaseId);
      if (phaseObj) return phaseObj.color;
    }
    switch (phase) {
      case "phase_1": return "#3b82f6";
      case "phase_2": return "#f59e0b";
      case "phase_3": return "#10b981";
      default: return "#64748b";
    }
  };

  const getPhaseLabel = (phase, phaseId, phaseName) => {
    if (phaseName) return phaseName;
    if (phaseId) {
      const phaseObj = phases.find(p => p.id === phaseId);
      if (phaseObj) return phaseObj.name;
    }
    switch (phase) {
      case "phase_1": return "Configuration & Physical Setup";
      case "phase_2": return "Precision Wiring";
      case "phase_3": return "Functional, Display & Data";
      default: return phase;
    }
  };

  const handlePhaseChange = (phaseId) => {
    const selectedPhase = phases.find(p => p.id === phaseId);
    if (selectedPhase) {
      setFormData({ ...formData, phase_id: phaseId, phase: selectedPhase.code });
    }
  };

  const selectedProd = products.find(p => p.id === selectedProduct);
  const selectedProdType = productTypes.find(t => t.id === selectedProd?.product_type_id);
  const productInputFields = (() => {
    const fieldOptionConfig = selectedProd?.field_option_config || {};
    const customFields = (selectedProdType?.default_test_input_fields || []).map(f =>
      f.type === "select" && fieldOptionConfig[f.key]?.length > 0
        ? { ...f, options: fieldOptionConfig[f.key] }
        : f
    );
    const powerOptions = selectedProd?.power_options || [];
    const powerField = powerOptions.length > 0
      ? [{ key: "power_supply", label: "Power Supply", type: "select", options: powerOptions }]
      : [];
    return [...powerField, ...customFields];
  })();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="steps-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Verification Steps</h2>
          <p className="text-muted-foreground">Configure the {steps.length}-step verification process</p>
        </div>

        <div className="flex items-center gap-3">
          <Popover open={productPickerOpen} onOpenChange={setProductPickerOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={productPickerOpen}
                className="w-[280px] justify-between font-normal"
                data-testid="select-product-filter"
              >
                {selectedProd ? (
                  <span className="flex items-center gap-1.5 min-w-0">
                    <span className="truncate">{selectedProd.name}</span>
                    {selectedProd.model_name && (
                      <span className="text-xs text-muted-foreground font-mono shrink-0">({selectedProd.model_name})</span>
                    )}
                  </span>
                ) : (
                  <span className="text-muted-foreground">Select product</span>
                )}
                <ChevronsUpDown className="w-4 h-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
              <Command filter={(value, search) => value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0}>
                <CommandInput placeholder="Search products…" data-testid="steps-product-search-input" />
                <CommandList>
                  <CommandEmpty>No product found.</CommandEmpty>
                  <CommandGroup>
                    {products.map((product) => (
                      <CommandItem
                        key={product.id}
                        value={`${product.name} ${product.model_name || ""}`}
                        onSelect={() => {
                          setSelectedProduct(product.id);
                          setProductPickerOpen(false);
                        }}
                        data-testid={`steps-product-option-${product.id}`}
                      >
                        <span className="truncate">{product.name}</span>
                        {product.model_name && (
                          <span className="text-xs text-muted-foreground font-mono shrink-0 ml-1.5">({product.model_name})</span>
                        )}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>

<Button onClick={() => handleOpenDialog()} disabled={!selectedProduct} data-testid="add-step-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Step
          </Button>
        </div>
      </div>

      <StepFormDialog
        open={showDialog}
        onOpenChange={setShowDialog}
        editingStep={editingStep}
        formData={formData}
        setFormData={setFormData}
        phases={phases}
        cvTemplates={cvTemplates}
        fileInputRef={fileInputRef}
        submitting={submitting}
        onImageUpload={handleImageUpload}
        onPhaseChange={handlePhaseChange}
        onSubmit={handleSubmit}
        token={token}
        productInputFields={productInputFields}
        reportTemplate={selectedProdType?.report_template || null}
      />

      {steps.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <ListChecks className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">No verification steps yet</p>
              <p className="text-sm text-muted-foreground">Add steps to define the verification process</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {steps.map((step) => (
            <StepCard
              key={step.id}
              step={step}
              expanded={expandedStep === step.id}
              onToggle={() => setExpandedStep(expandedStep === step.id ? null : step.id)}
              onEdit={handleOpenDialog}
              onDelete={handleDelete}
              onQuickRefUpload={handleQuickRefUpload}
              uploadingRef={uploadingRef}
              getPhaseColor={getPhaseColor}
              getPhaseLabel={getPhaseLabel}
              productInputFields={productInputFields}
            />
          ))}
        </div>
      )}
    </div>
  );
}
